<template>
  <div class="aviasales">
    <section class="aviasales__panel">
      <div v-if="allowRoundTrip" class="aviasales__trip-types">
        <app-radio v-model="form.tripType" value="one-way" :name="`${instanceId}-trip-type`">
          <template #label>One way</template>
        </app-radio>
        <app-radio v-model="form.tripType" value="return" :name="`${instanceId}-trip-type`">
          <template #label>Return</template>
        </app-radio>
      </div>

      <form @submit.prevent="onSearch">
        <div class="aviasales__route-row">
          <app-form-group label="From" :label-for="`${instanceId}-from`" required>
            <app-select
              :id="`${instanceId}-from`"
              v-model="form.from"
              :options="airportOptions"
              :clearable="false"
              :disabled="aerodromesLoading || !airportOptions.length"
              :loading="aerodromesLoading"
              placeholder="Select origin"
            />
          </app-form-group>
          <button @click="onSwapAirports" class="aviasales__swap" type="button" aria-label="Swap airports">⇄</button>
          <app-form-group label="To" :label-for="`${instanceId}-to`" required>
            <app-select
              :id="`${instanceId}-to`"
              v-model="form.to"
              :options="airportOptions"
              :clearable="false"
              :disabled="aerodromesLoading || !airportOptions.length"
              :loading="aerodromesLoading"
              placeholder="Select destination"
            />
          </app-form-group>
        </div>

        <div class="aviasales__details-row">
          <app-form-group class="aviasales__details-field" label="Airline">
            <app-select
              v-model="form.airline"
              :options="searchAirlineOptions"
              :loading="airlinesLoading"
              :disabled="airlinesLoading || !searchAirlineOptions.length"
              placeholder="All airlines"
            />
          </app-form-group>
          <app-form-group class="aviasales__details-field" label="Departure">
            <app-date-picker
              v-model="form.departure"
              :clearable="false"
              :disabled-date="disablePastDates"
              popup-class="datepicker__popup aviasales-datepicker-popup"
            />
          </app-form-group>
          <app-form-group v-if="allowRoundTrip" class="aviasales__details-field" label="Return">
            <app-date-picker
              v-model="form.returnDate"
              :clearable="false"
              :disabled="form.tripType === 'one-way'"
              :disabled-date="disableReturnDates"
              :default-value="form.departure"
              popup-class="datepicker__popup aviasales-datepicker-popup"
            />
          </app-form-group>
          <app-form-group label="Adults" class="aviasales__passenger-field form-group--no-indent" required>
            <app-input v-model.number="form.adults" type="number" min="1" max="9" step="1" required />
          </app-form-group>
          <app-form-group label="Children" class="aviasales__passenger-field form-group--no-indent" required>
            <app-input v-model.number="form.children" type="number" min="0" max="9" step="1" required />
          </app-form-group>
          <app-form-group label="Infants" class="aviasales__passenger-field form-group--no-indent" required>
            <app-input v-model.number="form.infants" type="number" min="0" max="9" step="1" required />
          </app-form-group>
          <app-button class="aviasales__search-button" type="submit" :disabled="!canSearch || searchLoading">
            {{ searchLoading ? 'Searching...' : 'Search flights' }}
          </app-button>
        </div>
      </form>
    </section>

    <div v-if="aerodromesError" class="aviasales__search-error" role="alert">
      {{ aerodromesError }}
      <app-button @click.native="loadAerodromes" :disabled="aerodromesLoading" size="small">Retry</app-button>
    </div>
    <div v-if="searchError" class="aviasales__search-error">{{ searchError }}</div>
    <div v-if="airlinesError" class="aviasales__search-error" role="alert">
      {{ airlinesError }}
      <app-button @click.native="loadAirlines" :disabled="airlinesLoading" size="small">Retry</app-button>
    </div>

    <div v-if="selectedOutboundFare && form.tripType === 'return'" class="aviasales__selection">
      <div
        class="aviasales__selected-legs"
        :class="{ 'aviasales__selected-legs--return': selectedInboundFare }"
      >
        <div class="aviasales__selected-leg">
          <div class="aviasales__selected-leg-heading">
            <span class="aviasales__selected-leg-label aviasales__selected-leg-label--outbound">Outbound</span>
            <strong>{{ selectedOutboundFlight.flightNumber }} · {{ selectedOutboundFare.name }}</strong>
          </div>
          <div class="aviasales__selected-leg-meta">
            <span>{{ selectedOutboundFlight.from }} → {{ selectedOutboundFlight.to }}</span>
            <span>{{ formatDate(selectedOutboundFlight.departure) }}</span>
          </div>
        </div>
        <div v-if="selectedInboundFare" class="aviasales__selected-leg">
          <div class="aviasales__selected-leg-heading">
            <span class="aviasales__selected-leg-label aviasales__selected-leg-label--return">Return</span>
            <strong>{{ selectedInboundFlight.flightNumber }} · {{ selectedInboundFare.name }}</strong>
          </div>
          <div class="aviasales__selected-leg-meta">
            <span>{{ selectedInboundFlight.from }} → {{ selectedInboundFlight.to }}</span>
            <span>{{ formatDate(selectedInboundFlight.departure) }}</span>
          </div>
        </div>
      </div>
      <div class="aviasales__selection-controls">
        <div class="aviasales__selection-price aviasales-total-badge">{{ formatMoney(selectedTripTotal, selectedCurrency) }}</div>
        <app-button @click.native="resetSelectedFlights" theme="dark" size="small">Reset selection</app-button>
      </div>
    </div>

    <template v-if="hasSearched">
      <div :class="{ 'aviasales__results-heading': form.tripType === 'return' }">
        <div
          class="aviasales__summary"
          :class="{ 'aviasales__summary--standalone': form.tripType === 'one-way' }"
        >
          <div>
            <strong>{{ form.from.name }} → {{ form.to.name }}</strong>
            <span>{{ formatSearchDate(form.departure) }} · {{ passengersCount }} {{ passengersCount === 1 ? 'passenger' : 'passengers' }}</span>
          </div>
          <span>{{ filteredFlights.length }} of {{ flightsForCurrentStep.length }} flights</span>
        </div>

        <div v-if="form.tripType === 'return'" class="aviasales__step-heading">
          <div
            class="aviasales__step"
            :class="{ 'aviasales__step--active': currentStep === 'outbound' }"
          >
            <span class="aviasales__step-number">1</span>
            <h2>Choose an outbound flight</h2>
          </div>
          <img
            class="aviasales__step-arrow"
            src="@/assets/img/arrow-right-gray-icon.svg"
            alt=""
            aria-hidden="true"
          >
          <div
            class="aviasales__step"
            :class="{ 'aviasales__step--active': currentStep === 'inbound' }"
          >
            <span class="aviasales__step-number">2</span>
            <h2>Choose a return flight</h2>
          </div>
        </div>
      </div>

      <div v-if="noInboundFlights" class="aviasales__empty-return">
        <strong>No return flights are available with {{ selectedOutboundFlight.airline }}</strong>
        <span>A round trip booking must use the same airline for both flights.</span>
        <app-button @click.native="resetSelectedFlights" theme="dark">Reset outbound flight</app-button>
      </div>

      <div v-else class="aviasales__results-layout" :class="{ 'aviasales__results-layout--no-filters': !showFilters }">
        <aside v-if="showFilters" class="aviasales__filters">
          <h2 class="aviasales__filters-title">Filters</h2>
          <app-form-group label="Sort by">
            <app-select v-model="filters.sort" :options="sortOptions" :clearable="false" />
          </app-form-group>
          <app-form-group label="Airline">
            <app-select v-model="filters.airline" :options="airlineOptions" placeholder="All airlines" />
          </app-form-group>
          <app-form-group label="Departure time">
            <div class="departure-time-filter">
              <div v-if="hasSingleDepartureTime" class="departure-time-filter__single">
                <strong>{{ formatMinutes(departureTimeBounds.min) }}</strong>
                <span>Only available time</span>
              </div>
              <template v-else>
                <div class="departure-time-filter__values">
                  <span>{{ formatMinutes(filters.departureFrom) }}</span>
                  <span>{{ formatMinutes(filters.departureTo) }}</span>
                </div>
                <div class="departure-time-filter__slider">
                  <div class="departure-time-filter__track"></div>
                  <div class="departure-time-filter__range" :style="departureTimeRangeStyle"></div>
                  <input
                    v-model.number="filters.departureFrom"
                    @input="onDepartureFromInput"
                    :min="departureTimeBounds.min"
                    :max="departureTimeBounds.max"
                    type="range"
                    step="1"
                    aria-label="Earliest departure time"
                  >
                  <input
                    v-model.number="filters.departureTo"
                    @input="onDepartureToInput"
                    :min="departureTimeBounds.min"
                    :max="departureTimeBounds.max"
                    type="range"
                    step="1"
                    aria-label="Latest departure time"
                  >
                </div>
              </template>
            </div>
          </app-form-group>
          <app-button @click.native="resetFilters" theme="dark" size="small">Reset filters</app-button>
        </aside>

        <div class="aviasales__results">
          <div v-for="flight in filteredFlights" :key="flight.id" class="flight-offer">
            <div v-if="flight.id === cheapestFlightId || flight.id === fastestFlightId" class="flight-card__badges">
              <span v-if="flight.id === cheapestFlightId" class="flight-card__badge flight-card__badge--cheapest">Cheapest</span>
              <span v-if="flight.id === fastestFlightId" class="flight-card__badge flight-card__badge--fastest">Fastest</span>
            </div>
            <article
              @click="toggleFares(flight)"
              @keydown.enter="toggleFares(flight)"
              @keydown.space.prevent="toggleFares(flight)"
              :aria-expanded="isFlightExpanded(flight) ? 'true' : 'false'"
              class="flight-card"
              :class="{ 'flight-card--expanded': expandedFlightId === flight.id }"
              role="button"
              tabindex="0"
            >
              <div class="flight-card__airline">
                <div class="flight-card__logo">
                  <img :src="flight.logo" :alt="flight.airline" @error="onLogoError">
                  <span>{{ airlineInitials(flight.airline) }}</span>
                </div>
                <div>
                  <strong>{{ flight.airline }}</strong>
                  <span>{{ flight.flightNumber }}<template v-if="flight.aircraft"> · {{ flight.aircraft }}</template></span>
                </div>
              </div>

              <div class="flight-card__route">
                <div>
                  <strong>{{ formatTime(flight.departure) }}</strong>
                  <span>{{ flight.from }}</span>
                </div>
                <div class="flight-card__duration">
                  <span>{{ formatDuration(flight.duration) }}</span>
                  <i></i>
                  <span>{{ flight.via || flight.type }}</span>
                </div>
                <div>
                  <strong>{{ formatTime(flight.arrival) }}</strong>
                  <span>{{ flight.to }}</span>
                </div>
              </div>

              <div class="flight-card__availability">
                <span>Baggage {{ flight.baggageOptionsCount > 1 ? 'from ' : '' }}{{ flight.minBaggage }}</span>
                <span :class="seatAvailabilityClass(flight.maxSeats)">{{ seatAvailabilityLabel(flight.maxSeats) }}</span>
              </div>

              <div class="flight-card__price">
                <span v-if="flight.priceOptionsCount > 1">from</span>
                <strong>{{ formatMoney(flight.minPrice, flight.minPriceCurrency) }}</strong>
              </div>
            </article>

            <transition name="fare-expand">
              <div v-if="isFlightExpanded(flight)" class="flight-card__fares">
                <div v-if="flight.rules.length" class="booking-rules">
                  <strong>Booking rules</strong>
                  <ul>
                    <li v-for="(rule, ruleIndex) in flight.rules" :key="ruleIndex">{{ rule }}</li>
                  </ul>
                </div>
                <div class="aviasales-fares__list">
                  <div
                    v-for="(fare, fareIndex) in flight.fares"
                    :key="fare.id || `${fare.flightId}-${fare.code}-${fareIndex}`"
                    @click.stop="selectFare(flight, fare)"
                    class="fare-card"
                    :class="{
                      'fare-card--selected': isSelectedFare(flight, fare),
                      'fare-card--selectable': canBookFlight(flight),
                      'fare-card--disabled': canBookFlight(flight) && (!fare.seats || validationLoading)
                    }"
                  >
                    <div class="fare-card__heading">
                      <div>
                        <strong>{{ fare.code }}</strong>
                        <span>· Baggage: <strong>{{ fare.baggage }}</strong></span>
                      </div>
                      <div class="fare-card__details">
                        <span>Adult: <strong>{{ formatCalculatedPrice(fare, 'adult', 'unitFare') }}</strong></span>
                        <span>Adult Tax: <strong>{{ formatCalculatedPrice(fare, 'adult', 'unitTax') }}</strong></span>
                        <template v-if="Number(form.children) > 0">
                          <span>Child: <strong>{{ formatCalculatedPrice(fare, 'child', 'unitFare') }}</strong></span>
                          <span>Child Tax: <strong>{{ formatCalculatedPrice(fare, 'child', 'unitTax') }}</strong></span>
                        </template>
                        <template v-if="Number(form.infants) > 0">
                          <span>Infant: <strong>{{ formatCalculatedPrice(fare, 'infant', 'unitFare') }}</strong></span>
                          <span>Infant Tax: <strong>{{ formatCalculatedPrice(fare, 'infant', 'unitTax') }}</strong></span>
                        </template>
                      </div>
                    </div>
                    <div class="flight-deadlines fare-card__deadlines">
                      <span><strong>Estimated PTL:</strong> {{ formatDeadline(fare.estimatedPtl, fare.estimatedAt) }}</span>
                      <span><strong>Estimated TTL:</strong> {{ formatDeadline(fare.estimatedTtl, fare.estimatedAt) }}</span>
                    </div>
                    <div class="fare-card__footer">
                      <span class="fare-card__seats" :class="seatAvailabilityClass(fare.seats)">{{ fare.seats ? `${fare.seats} seats` : 'Sold out' }}</span>
                      <div class="fare-card__purchase">
                        <div class="fare-card__total">
                          <strong class="aviasales-total-badge">{{ formatMoney(fare.segmentTotal, fare.currency) }}</strong>
                        </div>
                        <div
                          v-if="form.tripType === 'return' && canBookFlight(flight) && isSelectedFare(flight, fare)"
                          class="fare-card__actions"
                        >
                          <app-button
                            @click.native.stop="deselectFare(flight, fare)"
                            theme="dark"
                            size="small"
                          >Deselect fare</app-button>
                        </div>
                        <app-button
                          v-else-if="canBookFlight(flight) && !isSelectedFare(flight, fare)"
                          @click.native.stop="selectFare(flight, fare)"
                          :disabled="!fare.seats || validationLoading"
                          size="small"
                        >Select fare</app-button>
                      </div>
                    </div>
                    <div v-if="fare.notification" class="fare-card__notification">{{ fare.notification }}</div>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <app-no-results v-if="!filteredFlights.length" :text="emptyFlightsText" />
        </div>
      </div>
    </template>

    <modal
      :name="validationModalName"
      :width="800"
      height="auto"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      :focusTrap="true"
      :reset="true"
      @opened="focusValidation"
      @closed="restoreValidationFocus"
    >
      <div class="popup aviasales-validation" @keydown.tab.stop="trapValidationFocus" @keydown.esc.stop.prevent="cancelValidation" :aria-busy="validationLoading ? 'true' : 'false'">
        <button ref="validationClose" @click="cancelValidation" class="popup__close" type="button" aria-label="Close flight validation"></button>
        <div :id="`${instanceId}-validation-title`" class="popup__title">Flight validation</div>
        <div class="popup__body">
          <div v-if="validationLoading" class="validation-loading" role="status">
            <img src="@/assets/img/preloader-export.svg" alt="" width="40" height="40">
            <strong>Checking flights</strong>
            <span>Checking current flights, conditions and prices…</span>
          </div>
          <div v-else class="validation-result" :class="{ 'validation-result--changed': validationHasChanges, 'validation-result--error': validationError || validationCritical }" aria-live="polite">
            <strong class="validation-result__title"><svg v-if="validationError" class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 20h.01" /><path d="M8.5 16.429a5 5 0 0 1 7 0" /><path d="M5 12.859a10 10 0 0 1 5.17-2.69" /><path d="M19 12.859a10 10 0 0 0-2.007-1.523" /><path d="M2 8.82a15 15 0 0 1 4.177-2.643" /><path d="M22 8.82a15 15 0 0 0-11.288-3.764" /><path d="m2 2 20 20" /></svg><svg v-else-if="validationCritical || (validationHasChanges && !validationAccepted)" class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10" /><line x1="12" x2="12" y1="8" y2="12" /><line x1="12" x2="12.01" y1="16" y2="16" /></svg><svg v-else class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>{{ validationStatus }}</strong>
            <p v-if="validationError" role="alert">{{ validationError }}</p>
            <p v-else-if="validationCritical">The selected itinerary has a critical change. Return to results and select your flights again.</p>
            <p v-else-if="validationHasChanges">{{ validationAccepted ? 'Changes accepted for this flight check.' : 'Review and accept the changes before continuing.' }}</p>
          </div>

          <template v-if="currentValidation">
            <article v-for="leg in currentValidation.legs" :key="leg.direction" class="validation-leg" :class="{ 'validation-leg--return': leg.direction === 'inbound' }">
              <div class="validation-leg__heading">
                <img v-if="leg.flight.airline && leg.flight.airline.logo_url" class="validation-leg__logo" :src="leg.flight.airline.logo_url" :alt="leg.flight.airline.name" @error="onLogoError">
                <span v-else class="validation-leg__emblem" aria-hidden="true"><svg  class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" /></svg></span>
                <div class="validation-leg__airline">
                  <strong>{{ formatValidationValue(leg.flight.airline && leg.flight.airline.name) }}</strong>
                  <span>{{ formatValidationValue(leg.flight.number) }} · {{ validationDate(leg.flight.flightdate) }}</span>
                </div>
                <div class="validation-leg__badges">
                  <span v-if="leg.critical" class="validation-leg__status validation-leg__critical">Critical change</span>
                  <span v-else-if="leg.changed" class="validation-leg__status validation-leg__changed">Changed</span>
                  <span class="validation-leg__direction"><svg v-if="leg.direction === 'outbound'" class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M2 22h20" /><path d="M6.36 17.4 4 17l-2-4 1.1-.55a2 2 0 0 1 1.8 0l.17.1a2 2 0 0 0 1.8 0L8 12 5 6l.9-.45a2 2 0 0 1 2.09.2l4.02 3a2 2 0 0 0 2.1.2l4.19-2.06a2.41 2.41 0 0 1 1.73-.17L21 7a1.4 1.4 0 0 1 .87 1.99l-.38.76c-.23.46-.6.84-1.07 1.08L7.58 17.2a2 2 0 0 1-1.22.18Z" /></svg><svg v-else class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M2 22h20" /><path d="M3.77 10.77 2 9l2-4.5 1.1.55c.55.28.9.84.9 1.45s.35 1.17.9 1.45L8 8.5l3-6 1.05.53a2 2 0 0 1 1.09 1.52l.72 5.4a2 2 0 0 0 1.09 1.52l4.4 2.2c.42.22.78.55 1.01.96l.6 1.03c.49.88-.06 1.98-1.06 2.1l-1.18.15c-.47.06-.95-.02-1.37-.24L4.29 11.15a2 2 0 0 1-.52-.38Z" /></svg>{{ leg.title }}</span>
                </div>
              </div>
              <div class="validation-leg__route">
                <div>
                  <strong>{{ validationTime(leg.flight.departure) }}</strong>
                  <span v-if="leg.flight.provider_route && leg.flight.provider_route.from_code" class="validation-leg__code">{{ leg.flight.provider_route.from_code }}</span>
                  <span class="validation-leg__airport">{{ formatValidationValue(leg.flight.from && leg.flight.from.name) }}</span>
                </div>
                <span class="validation-leg__connection">
                  <span class="validation-leg__route-line" aria-hidden="true">
                    <!-- Plane icon from the supplied HTML reference (Lucide, ISC). -->
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false">
                      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
                    </svg>
                  </span>
                  <span v-if="leg.flight.type" class="validation-leg__flight-type">{{ formatValidationValue(leg.flight.type) }}</span>
                </span>
                <div>
                  <strong>{{ validationTime(leg.flight.arrival) }}</strong>
                  <span v-if="leg.flight.provider_route && leg.flight.provider_route.to_code" class="validation-leg__code">{{ leg.flight.provider_route.to_code }}</span>
                  <span class="validation-leg__airport">{{ formatValidationValue(leg.flight.to && leg.flight.to.name) }}</span>
                </div>
              </div>
              <div class="validation-leg__meta">
                <span v-if="leg.flight.bookable !== null && leg.flight.bookable !== undefined">Bookable: {{ formatValidationValue(leg.flight.bookable) }}</span>
                <span><svg  class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3" /><path d="M3 16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V11a2 2 0 0 0-4 0z" /><path d="M5 18v2" /><path d="M19 18v2" /></svg>Class: {{ formatValidationValue(leg.flight.class) }}</span>
                <span v-if="leg.flight.via" class="validation-leg__via">{{ formatValidationValue(leg.flight.via) }}</span>
                <span v-if="leg.flight.freeseats !== null && leg.flight.freeseats !== undefined"><svg  class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><path d="M16 3.128a4 4 0 0 1 0 7.744" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><circle cx="9" cy="7" r="4" /></svg>{{ leg.flight.freeseats }} seats available</span>
              </div>
              <div class="validation-leg__price"><span>All passengers · taxes included</span><strong>{{ validationMoney(leg.flight.calculatedPrice.segmentTotal, validatedCurrency) }}</strong></div>
              <div v-if="leg.differences.length" class="validation-differences">
                <div v-for="row in leg.differences" :key="row.field" class="validation-difference" :class="{ 'validation-leg__critical': row.critical }">
                  <strong>{{ row.label }}<template v-if="row.critical"> · Critical</template></strong>
                  <span><span class="validation-before">{{ row.searchValue }}</span> → <strong>{{ row.currentValue }}</strong></span>
                </div>
              </div>
              <p v-if="leg.changed && !leg.differences.length" class="validation-leg__unchanged">Conditions changed; comparison details were not provided.</p>
            </article>

            <details class="validation-details">
              <summary><span class="validation-chevron"><svg  class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m9 18 6-6-6-6" /></svg></span>All compared values</summary>
              <p>The server supplies before/after values for reported differences. Unreported fields are not confirmed as matched. Schedule and fare conditions are not included in this comparison.</p>
              <div v-for="leg in currentValidation.legs" :key="leg.direction">
                <h3 class="validation-direction" :class="{ 'validation-direction--return': leg.direction === 'inbound' }">{{ leg.title }}</h3>
                <div v-for="row in allValidationValues(leg)" :key="row.field" class="validation-difference">
                  <strong>{{ row.label }}</strong><span>{{ row.value }}</span>
                </div>
              </div>
            </details>
            <details class="validation-details">
              <summary><span class="validation-chevron"><svg  class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m9 18 6-6-6-6" /></svg></span>Fare conditions</summary>
              <div v-for="leg in currentValidation.legs" :key="leg.direction">
                <h3 class="validation-direction" :class="{ 'validation-direction--return': leg.direction === 'inbound' }">{{ leg.title }}</h3>
                <p class="validation-terms">{{ validationTerms(leg.flight.terms) }}</p>
              </div>
            </details>
            <details class="validation-details">
              <summary><span class="validation-chevron"><svg  class="validation-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m9 18 6-6-6-6" /></svg></span>Price breakdown</summary>
              <div v-for="group in validationPriceGroups" :key="group.key" class="validation-breakdown">
                <table>
                  <caption>{{ group.title }}</caption>
                  <thead>
                    <tr>
                      <th scope="col">Passengers</th>
                      <th scope="col">Fare per passenger</th>
                      <th scope="col">Taxes per passenger</th>
                      <th scope="col">Total per passenger</th>
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="category in group.rows" :key="category.type">
                      <th scope="row">{{ category.label }} × {{ category.quantity }}</th>
                      <td>{{ category.fare }}</td>
                      <td>{{ category.tax }}</td>
                      <td>{{ category.unitTotal }}</td>
                      <td><strong>{{ category.total }}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </template>

          <div v-if="!validationLoading" class="validation-total">
            <div><span>Total · all passengers, taxes included</span><strong>{{ currentValidation ? validationMoney(validatedTotal, validatedCurrency) : '—' }}</strong></div>
            <div v-if="currentValidation" class="flight-deadlines">
              <span><b>Estimated PTL:</b> {{ formatDeadline(currentValidation.estimatedPtl, currentValidation.estimatedAt) }}</span>
              <span><b>Estimated TTL:</b> {{ formatDeadline(currentValidation.estimatedTtl, currentValidation.estimatedAt) }}</span>
            </div>
          </div>
          <div v-if="canSelect" class="validation-actions">
            <app-button @click.native="cancelValidation" :disabled="Boolean(bookingLoading)" theme="dark">Back to results</app-button>
            <app-button v-if="validationError" @click.native="validateSelection" :disabled="validationLoading">Retry validation</app-button>
            <app-button v-else-if="validationHasChanges && !validationCritical && !validationAccepted" @click.native="acceptValidationChanges" :disabled="validationLoading">Accept changes</app-button>
            <template v-else-if="bookingLeadId">
              <app-button @click.native="createBooking('auto')" :disabled="!canContinueValidation || Boolean(bookingLoading)">{{ bookingLoading === 'auto' ? 'Booking...' : 'Book Flight (Auto)' }}</app-button>
              <app-button @click.native="createBooking('manual')" :disabled="!canContinueValidation || Boolean(bookingLoading)" theme="info">{{ bookingLoading === 'manual' ? 'Booking...' : 'Book Flight (Manual)' }}</app-button>
            </template>
            <app-button v-else @click.native="onBookingRequested" :disabled="!canContinueValidation">{{ bookingButtonLabel }}</app-button>
          </div>
          <p v-if="bookingError" class="aviasales__search-error" role="alert">{{ bookingError }}</p>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getFlightsAerodromes, getFlightsAirlines, getFlightsSearch, postFlightsGetFlight, postFlightsCreateBooking, postFlightsCreateManualBooking } from '@/http'
import { comparisonLabels, formatValue, formatAmount, formatDeadline, normalizeValidation } from '@/views/flights/aviasales/flight-validation'
import { normalizeDeeplink } from '@/views/flights/aviasales/deeplink-adapter'

const normalizeAerodromes = payload => {
  let aerodromes = payload

  if (!Array.isArray(aerodromes)) {
    aerodromes = payload?.results || payload?.aerodromes || payload?.items || payload?.data || []
  }
  if (!Array.isArray(aerodromes)) aerodromes = Object.values(aerodromes)

  return aerodromes.map(aerodrome => {
    if (typeof aerodrome === 'string') return { code: aerodrome, name: aerodrome }

    const code = aerodrome.iata_code || aerodrome.icao_code
    const originalName = aerodrome.name
      || aerodrome.title
      || aerodrome.aerodrome_name
      || aerodrome.airport_name
      || aerodrome.label
      || code
    const normalizedCode = code === null || code === undefined ? '' : String(code)
    const name = originalName === null || originalName === undefined ? '' : String(originalName).replace(/\s+\([A-Z0-9]{3,4}\)$/, '')

    return { ...aerodrome, code: normalizedCode, name }
  }).filter(aerodrome => Number.isInteger(aerodrome.id) && aerodrome.id > 0 && aerodrome.name)
}

const formatDateValue = date => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-')

export default {
  name: 'FlightBookingFlow',
  props: {
    allowRoundTrip: { type: Boolean, default: true },
    canSelect: { type: Boolean, default: false },
    initialSearch: { type: Object, default: () => ({}) },
    showFilters: { type: Boolean, default: true },
    bookingLeadId: { type: Number, default: null },
    bookingButtonLabel: { type: String, default: 'Continue' }
  },
  data() {
    const departureDate = new Date()
    departureDate.setDate(departureDate.getDate() + 1)
    return {
      instanceId: `flight-flow-${this._uid}`,
      airportOptions: [],
      searchAirlineOptions: [],
      airlinesLoading: false,
      airlinesError: '',
      airlinesRequestId: 0,
      bookingLoading: false,
      bookingError: '',
      aerodromesError: '',
      destroyed: false,
      aerodromesRequestId: 0,
      aerodromesLoading: false,
      sortOptions: [
        { code: 'departure', name: 'Departure time' },
        { code: 'price', name: 'Lowest price' },
        { code: 'duration', name: 'Shortest duration' }
      ],
      form: {
        tripType: this.allowRoundTrip && this.initialSearch.tripType === 'return' ? 'return' : 'one-way',
        from: null,
        to: null,
        airline: null,
        departure: this.initialSearch.departure || formatDateValue(departureDate),
        returnDate: this.allowRoundTrip ? this.initialSearch.returnDate || '' : '',
        adults: this.initialSearch.adults === undefined ? 1 : this.initialSearch.adults,
        children: this.initialSearch.children === undefined ? 0 : this.initialSearch.children,
        infants: this.initialSearch.infants === undefined ? 0 : this.initialSearch.infants
      },
      filters: {
        sort: { code: 'departure', name: 'Departure time' },
        airline: null,
        departureFrom: 0,
        departureTo: 1439
      },
      flights: [],
      hasSearched: false,
      searchLoading: false,
      searchError: '',
      validationError: '',
      validationLoading: false,
      validationCloseRequested: false,
      acceptedValidationSnapshotId: null,
      validationRequestId: 0,
      validationRequestKey: null,
      currentValidation: null,
      searchRequestId: 0,
      searchSnapshotId: null,
      expandedFlightId: null,
      selectedOutboundFlight: null,
      selectedOutboundFare: null,
      selectedInboundFlight: null,
      selectedInboundFare: null
    }
  },
  created() {
    this.loadAerodromes()
    this.loadAirlines()
  },
  beforeDestroy() {
    this.destroyed = true
    this.airlinesRequestId += 1
    this.searchRequestId += 1
    this.invalidateValidation()
    this.$modal.hide(this.validationModalName)
  },
  computed: {
    validationModalName() {
      return `${this.instanceId}-validation`
    },
    canSearch() {
      const isValidPassengerCount = (value, minimum) => value !== null
        && value !== undefined
        && value !== ''
        && Number.isInteger(Number(value))
        && Number(value) >= minimum
        && Number(value) <= 9

      return Boolean(this.form.from
        && this.form.to
        && this.form.departure
        && (this.form.tripType !== 'return' || this.form.returnDate)
        && isValidPassengerCount(this.form.adults, 1)
        && isValidPassengerCount(this.form.children, 0)
        && isValidPassengerCount(this.form.infants, 0))
    },
    passengersCount() {
      return Number(this.form.adults || 0) + Number(this.form.children || 0) + Number(this.form.infants || 0)
    },
    passengerComposition() {
      const passengers = [
        { count: Number(this.form.adults || 0), singular: 'adult', plural: 'adults' },
        { count: Number(this.form.children || 0), singular: 'child', plural: 'children' },
        { count: Number(this.form.infants || 0), singular: 'infant', plural: 'infants' }
      ]

      return passengers
        .filter(passenger => passenger.count > 0)
        .map(passenger => `${passenger.count} ${passenger.count === 1 ? passenger.singular : passenger.plural}`)
        .join(' · ')
    },
    airlineOptions() {
      return [...new Set(this.flightsForCurrentStep.map(flight => flight.airline))]
        .sort()
        .map(airline => ({ code: airline, name: airline }))
    },
    currentStep() {
      return this.form.tripType === 'return' && this.selectedOutboundFare ? 'inbound' : 'outbound'
    },
    outboundFlights() {
      return this.flights.filter(flight => flight.direction !== 'inbound'
        && this.matchesAirport(flight, this.form.from, 'from')
        && this.matchesAirport(flight, this.form.to, 'to')
        && formatDateValue(flight.departure) === this.form.departure)
    },
    inboundFlights() {
      if (!this.selectedOutboundFlight) return []

      return this.flights.filter(flight => {
        const sameAirline = this.selectedOutboundFlight.airlineId !== null ? flight.airlineId === this.selectedOutboundFlight.airlineId : flight.airline === this.selectedOutboundFlight.airline

        return flight.direction === 'inbound'
          && this.matchesAirport(flight, this.form.to, 'from')
          && this.matchesAirport(flight, this.form.from, 'to')
          && formatDateValue(flight.departure) === this.form.returnDate
          && sameAirline
      })
    },
    flightsForCurrentStep() {
      return this.currentStep === 'inbound' ? this.inboundFlights : this.outboundFlights
    },
    noInboundFlights() {
      return this.currentStep === 'inbound' && !this.inboundFlights.length
    },
    emptyFlightsText() {
      if (!this.flightsForCurrentStep.length) return 'No flights are available for the selected route and date'
      return this.currentStep === 'inbound' ? 'No return flights match the selected filters' : 'No outbound flights match the selected filters'
    },
    departureTimeBounds() {
      const departures = this.flightsForCurrentStep
        .map(flight => (flight.departure.getHours() * 60) + flight.departure.getMinutes())

      if (!departures.length) return { min: 0, max: 1439 }

      return {
        min: Math.min(...departures),
        max: Math.max(...departures)
      }
    },
    hasSingleDepartureTime() {
      return Boolean(this.flightsForCurrentStep.length
        && this.departureTimeBounds.min === this.departureTimeBounds.max)
    },
    departureTimeRangeStyle() {
      const { min, max } = this.departureTimeBounds
      if (min === max) return { left: '0%', right: '0%' }

      const range = max - min
      const left = ((this.filters.departureFrom - min) / range) * 100
      const right = 100 - (((this.filters.departureTo - min) / range) * 100)

      return { left: `${left}%`, right: `${right}%` }
    },
    validatedTotal() {
      return this.currentValidation ? this.currentValidation.total : null
    },
    validatedCurrency() {
      return this.currentValidation ? this.currentValidation.currency : null
    },
    validationHasChanges() {
      return Boolean(this.currentValidation && this.currentValidation.comparison.changed)
    },
    validationCritical() {
      return Boolean(this.currentValidation && this.currentValidation.comparison.critical)
    },
    validationAccepted() {
      return Boolean(this.currentValidation && this.acceptedValidationSnapshotId === this.currentValidation.flightSnapshotId)
    },
    validationStatus() {
      if (this.validationLoading) return 'Checking flights'
      if (this.validationError) return 'Unable to validate flights'
      if (this.validationCritical) return 'Selected itinerary changed'
      if (this.validationAccepted) return 'Changes accepted'
      if (this.validationHasChanges) return 'Flight conditions changed'
      return 'No changes in compared values'
    },
    canContinueValidation() {
      return Boolean(this.currentValidation && this.currentValidation.flightSnapshotId
        && this.validationRequestKey === this.currentSelectionKey()
        && !this.validationLoading && !this.validationError && !this.validationCritical
        && (!this.validationHasChanges || this.validationAccepted))
    },
    validationBreakdown() {
      if (!this.currentValidation) return []
      return this.validationPriceRows(this.currentValidation.price)
    },
    validationPriceGroups() {
      if (!this.currentValidation) return []
      const groups = [{ key: 'itinerary', title: 'All selected flights', rows: this.validationBreakdown }]
      if (this.currentValidation.legs.length > 1) {
        this.currentValidation.legs.forEach(leg => groups.push({
          key: leg.direction,
          title: `${leg.title} · ${formatValue(leg.flight.number)}`,
          rows: this.validationPriceRows(leg.flight.calculatedPrice)
        }))
      }
      return groups
    },
    selectedCurrency() {
      return this.selectedOutboundFare?.currency || 'USD'
    },
    selectedTripTotal() {
      const outboundTotal = this.selectedOutboundFare ? this.totalPrice(this.selectedOutboundFare) : 0
      const inboundTotal = this.selectedInboundFare ? this.totalPrice(this.selectedInboundFare) : 0
      return outboundTotal + inboundTotal
    },
    cheapestFlightId() {
      const cheapestFlight = this.filteredFlights.reduce((cheapest, flight) => {
        if (!cheapest || flight.minPrice < cheapest.minPrice) return flight
        return cheapest
      }, null)

      return cheapestFlight ? cheapestFlight.id : null
    },
    fastestFlightId() {
      const fastestFlight = this.filteredFlights.reduce((fastest, flight) => {
        if (!fastest || flight.duration < fastest.duration) return flight
        return fastest
      }, null)

      return fastestFlight ? fastestFlight.id : null
    },
    filteredFlights() {
      const flights = this.flightsForCurrentStep.filter(flight => {
        const departureMinutes = (flight.departure.getHours() * 60) + flight.departure.getMinutes()
        const airlineMatches = !this.filters.airline || flight.airline === this.filters.airline.code
        const timeMatches = departureMinutes >= this.filters.departureFrom
          && departureMinutes <= this.filters.departureTo

        return airlineMatches && timeMatches
      })

      const sortField = this.filters.sort.code
      return flights.sort((first, second) => {
        if (sortField === 'price') return first.minPrice - second.minPrice
        if (sortField === 'duration') return first.duration - second.duration
        return first.departure - second.departure
      })
    }
  },
  watch: {
    'form.tripType'(tripType) {
      this.form.returnDate = tripType === 'return' ? this.addDays(this.form.departure, 1) : ''
    },
    'form.departure'(departure) {
      if (this.form.tripType !== 'return' || !departure) return

      const minimumReturnDate = departure
      if (!this.form.returnDate || this.form.returnDate < minimumReturnDate) {
        this.form.returnDate = minimumReturnDate
      }
    },
    form: {
      deep: true,
      handler() {
        this.clearSearchResults()
      }
    }
  },
  methods: {
    async loadAirlines() {
      const requestId = ++this.airlinesRequestId
      this.airlinesLoading = true
      this.airlinesError = ''
      try {
        const response = await getFlightsAirlines()
        if (this.destroyed || requestId !== this.airlinesRequestId) return
        this.searchAirlineOptions = Array.isArray(response.data) ? response.data : []
        if (!this.searchAirlineOptions.length) this.airlinesError = 'No airlines available.'
      } catch (_) {
        if (this.destroyed || requestId !== this.airlinesRequestId) return
        this.airlinesError = 'Unable to load airlines. Please retry.'
      } finally {
        if (requestId === this.airlinesRequestId) this.airlinesLoading = false
      }
    },
    async loadAerodromes() {
      const requestId = ++this.aerodromesRequestId
      this.aerodromesLoading = true

      this.aerodromesError = ''
      try {
        const response = await getFlightsAerodromes()
        if (this.destroyed || requestId !== this.aerodromesRequestId) return
        const options = normalizeAerodromes(response.data)

        this.airportOptions = options
        this.form.from = options.find(option => option.id === this.initialSearch.fromId) || null
        this.form.to = options.find(option => option.id === this.initialSearch.toId) || null
        if (!options.length) this.aerodromesError = 'No airports available.'
      } catch (error) {
        if (this.destroyed || requestId !== this.aerodromesRequestId) return
        this.aerodromesError = error.response?.status === 401 ? 'The flight API requires a valid session for this environment.' : 'Unable to load airports. Please retry.'
        this.airportOptions = []
        this.form.from = null
        this.form.to = null
      } finally {
        if (requestId === this.aerodromesRequestId) this.aerodromesLoading = false
      }
    },
    async onSearch() {
      if (!this.canSearch) return
      const requestId = ++this.searchRequestId
      const params = {
        adults: Number(this.form.adults),
        child: Number(this.form.children),
        currency: 'USD',
        from: this.form.from.id,
        infant: Number(this.form.infants),
        start: this.form.departure,
        to: this.form.to.id
      }
      if (this.form.tripType === 'return') params.end = this.form.returnDate
      if (this.form.airline) params.airline = this.form.airline.id

      this.resetSelectedFlights()
      this.expandedFlightId = null
      this.hasSearched = false
      this.searchLoading = true
      this.searchError = ''
      this.searchSnapshotId = null

      try {
        const response = await getFlightsSearch(params)
        if (requestId !== this.searchRequestId) return

        this.searchSnapshotId = response.data?.snapshot_id || null
        this.flights = normalizeDeeplink(response.data, {
          outbound: this.form.departure,
          inbound: this.form.returnDate
        })
        this.resetDepartureTimeFilter()
        this.hasSearched = true
      } catch (error) {
        if (requestId !== this.searchRequestId) return

        this.flights = []
        this.hasSearched = false
        this.searchSnapshotId = null
        const responseData = error?.response?.data
        if (responseData) {
          if (typeof responseData === 'string') {
            this.searchError = responseData
          } else {
            this.searchError = responseData.details || responseData.detail || JSON.stringify(responseData)
          }
        } else {
          this.searchError = error.message || 'Unable to search flights. Please try again.'
        }
      } finally {
        if (requestId === this.searchRequestId) this.searchLoading = false
      }
    },
    clearSearchResults() {
      if (!this.hasSearched && !this.searchLoading && !this.searchError) return

      this.searchRequestId += 1
      this.hasSearched = false
      this.searchLoading = false
      this.searchError = ''
      this.searchSnapshotId = null
      this.flights = []
      this.resetSelectedFlights()
      this.expandedFlightId = null
    },
    onSwapAirports() {
      const from = this.form.from
      this.form.from = this.form.to
      this.form.to = from
    },
    toggleFares(flight) {
      this.expandedFlightId = this.expandedFlightId === flight.id ? null : flight.id
    },
    isFlightExpanded(flight) {
      return this.expandedFlightId === flight.id
    },
    async selectFare(flight, fare) {
      if (!this.canBookFlight(flight) || !fare.seats || this.validationLoading) return

      this.expandedFlightId = flight.id

      if (this.currentStep === 'outbound') {
        this.selectedOutboundFlight = flight
        this.selectedOutboundFare = fare

        if (this.form.tripType === 'return') {
          this.filters.airline = null
          this.expandedFlightId = null
          await this.$nextTick()
          this.resetDepartureTimeFilter()
          return
        }
      } else {
        this.selectedInboundFlight = flight
        this.selectedInboundFare = fare
      }

      await this.validateSelection()
    },
    deselectFare(flight, fare) {
      if (!this.isSelectedFare(flight, fare)) return

      this.invalidateValidation()
      if (flight.direction === 'inbound') {
        this.selectedInboundFlight = null
        this.selectedInboundFare = null
      } else {
        this.resetSelectedFlights()
      }
      this.expandedFlightId = flight.id
    },
    isSelectedFare(flight, fare) {
      const selectedFlight = flight.direction === 'inbound' ? this.selectedInboundFlight : this.selectedOutboundFlight
      const selectedFare = flight.direction === 'inbound' ? this.selectedInboundFare : this.selectedOutboundFare
      const sameFare = selectedFare === fare || Boolean(selectedFare
        && selectedFare.id !== null
        && selectedFare.id !== undefined
        && fare.id !== null
        && fare.id !== undefined
        && String(selectedFare.id) === String(fare.id))

      return Boolean(selectedFlight
        && selectedFlight.id === flight.id
        && sameFare)
    },
    isSelectedFlight(flight) {
      return this.selectedOutboundFlight?.id === flight.id || this.selectedInboundFlight?.id === flight.id
    },
    matchesAirport(flight, airport, direction) {
      if (!airport) return false
      return flight[`${direction}Id`] === airport.id
    },
    canBookFlight(flight) {
      return this.canSelect && flight.createBooking
    },
    resetSelectedFlights() {
      this.selectedOutboundFlight = null
      this.selectedOutboundFare = null
      this.selectedInboundFlight = null
      this.selectedInboundFare = null
      this.invalidateValidation()
      this.validationError = ''
      this.acceptedValidationSnapshotId = null
      this.expandedFlightId = null
      this.filters.airline = null
      if (this.hasSearched) this.$nextTick(() => this.resetDepartureTimeFilter())
    },
    getFlightPayload() {
      const createLeg = (flight, fare) => ({
        flight_id: fare.flightId,
        fare_id: fare.id,
        ...(fare.offerId ? { offer_id: fare.offerId } : {})
      })
      const payload = {
        search_snapshot_id: this.searchSnapshotId,
        trip_type: this.form.tripType === 'return' ? 'RT' : 'OW',
        from: this.form.from.id,
        to: this.form.to.id,
        adults: Number(this.form.adults),
        child: Number(this.form.children),
        infant: Number(this.form.infants),
        currency: this.selectedCurrency,
        outbound: createLeg(this.selectedOutboundFlight, this.selectedOutboundFare)
      }

      if (this.form.tripType === 'return') {
        payload.inbound = createLeg(this.selectedInboundFlight, this.selectedInboundFare)
      }

      return payload
    },
    currentSelectionKey() {
      if (!this.selectedOutboundFare || (this.form.tripType === 'return' && !this.selectedInboundFare)) return null
      return JSON.stringify({ request: this.getFlightPayload(), departure: this.form.departure, returnDate: this.form.returnDate })
    },
    invalidateValidation() {
      this.validationRequestId += 1
      this.validationRequestKey = null
      this.currentValidation = null
      this.validationLoading = false
      this.validationError = ''
      this.acceptedValidationSnapshotId = null
      this.bookingError = ''
    },
    async validateSelection() {
      if (this.validationLoading || !this.currentSelectionKey()) return
      const request = this.getFlightPayload()
      const requestKey = this.currentSelectionKey()
      const requestId = ++this.validationRequestId
      this.validationRequestKey = requestKey
      this.validationLoading = true
      this.validationError = ''
      this.acceptedValidationSnapshotId = null
      this.currentValidation = null
      if (!this.validationFocusTarget) {
        this.validationFocusTarget = document.activeElement
        this.validationFocusFallback = this.validationFocusTarget?.closest?.('.fare-card')
      }
      this.validationCloseRequested = false
      this.$modal.show(this.validationModalName)
      const isActive = () => requestId === this.validationRequestId && requestKey === this.currentSelectionKey()
      try {
        const response = await postFlightsGetFlight(request)
        if (!isActive()) return
        this.currentValidation = normalizeValidation(response.data, request)
      } catch (error) {
        if (!isActive()) return
        const details = error.response && error.response.data && error.response.data.details
        const safeDetails = typeof details === 'string' && details.length < 500
          && !/[<>]|traceback|authorization|password|token|secret/i.test(details)
        this.validationError = safeDetails ? details : 'Unable to check the selected flights. Please retry validation.'
      } finally {
        if (isActive()) {
          this.validationLoading = false
          this.$nextTick(() => {
            const close = this.$refs && this.$refs.validationClose
            if (isActive() && close && close.getClientRects().length && document.activeElement === document.body) {
              close.focus()
            }
          })
        }
      }
    },
    cancelValidation() {
      if (this.bookingLoading) return
      this.validationCloseRequested = true
      this.$modal.hide(this.validationModalName)
      if (this.form.tripType === 'one-way') {
        const expandedFlightId = this.expandedFlightId
        this.resetSelectedFlights()
        this.expandedFlightId = expandedFlightId
      }
    },
    trapValidationFocus(event) {
      // vue-js-modal caches focus targets when opened, before async content arrives.
      const targets = Array.from(event.currentTarget.querySelectorAll('button:not([disabled]), summary'))
        .filter(element => element.getClientRects().length)
      const target = event.shiftKey ? targets[targets.length - 1] : targets[0]
      const boundary = event.shiftKey ? targets[0] : targets[targets.length - 1]
      if (target && document.activeElement === boundary) {
        event.preventDefault()
        target.focus()
      }
    },
    focusValidation() {
      if (this.validationCloseRequested) {
        this.$modal.hide(this.validationModalName)
        return
      }
      if (!this.$refs.validationClose) return
      const dialog = this.$refs.validationClose.closest('[role="dialog"]')
      if (dialog) dialog.setAttribute('aria-labelledby', `${this.instanceId}-validation-title`)
      this.$refs.validationClose.focus()
    },
    restoreValidationFocus() {
      if (this.validationFocusTarget && this.validationFocusTarget.isConnected) this.validationFocusTarget.focus()
      else if (this.validationFocusFallback?.isConnected) {
        const target = this.validationFocusFallback.querySelector('button:not([disabled])')
        if (target) target.focus()
      }
      this.validationFocusTarget = null
      this.validationFocusFallback = null
    },
    acceptValidationChanges() {
      if (!this.currentValidation || this.validationLoading || this.validationError || this.validationCritical
        || this.validationRequestKey !== this.currentSelectionKey()) return
      this.acceptedValidationSnapshotId = this.currentValidation.flightSnapshotId
    },
    validationMoney: formatAmount,
    formatDeadline,
    formatValidationValue: formatValue,
    validationAirport(value) {
      if (!value || typeof value !== 'object') return formatValue(value)
      return [value.code, value.name].filter(Boolean).join(' · ') || 'Not provided'
    },
    validationTime(value) {
      return typeof value === 'string' && /^\d{2}:\d{2}/.test(value) ? value.slice(0, 5) : formatValue(value)
    },
    validationDate(value) {
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Not provided'
      const date = new Date(`${value}T00:00:00`)
      return Number.isNaN(date.getTime()) ? 'Not provided' : this.formatDate(date)
    },
    validationTerms(value) {
      if (value === null || value === undefined || value === '') return 'Fare conditions not provided.'
      let text = typeof value === 'string' ? value : JSON.stringify(value)
      try { text = decodeURIComponent(text) } catch { /* Provider terms may already be decoded. */ }
      const parsed = new DOMParser().parseFromString(text, 'text/html')
      parsed.querySelectorAll('script, style, iframe, object').forEach(node => node.remove())
      parsed.querySelectorAll('br').forEach(node => node.replaceWith(parsed.createTextNode('\n')))
      parsed.querySelectorAll('p, div, li, h1, h2, h3, tr').forEach(node => node.appendChild(parsed.createTextNode('\n')))
      return parsed.body.textContent.trim() || 'Fare conditions not provided.'
    },
    validationPriceRows(price) {
      return ['adult', 'child', 'infant'].map(type => {
        const category = price[type]
        return {
          type,
          label: { adult: 'Adults', child: 'Children', infant: 'Infants' }[type],
          quantity: category.quantity,
          fare: formatAmount(category.unitFare, price.currency),
          tax: formatAmount(category.unitTax, price.currency),
          unitTotal: formatAmount(category.unitTotal, price.currency),
          total: formatAmount(category.total, price.currency)
        }
      }).filter(category => category.quantity > 0)
    },
    allValidationValues(leg) {
      const labels = { ...comparisonLabels }
      leg.differences.forEach(row => { labels[row.field] = row.label })
      return Object.entries(labels).map(([field, label]) => {
        const difference = leg.differences.find(row => row.field === field)
        return { field, label, value: difference ? `${difference.searchValue} → ${difference.currentValue}` : 'No comparison values provided' }
      })
    },
    onBookingRequested() {
      if (!this.canSelect || !this.canContinueValidation) return
      this.$emit('booking-requested', {
        searchSnapshotId: this.searchSnapshotId,
        flightSnapshotId: this.currentValidation.flightSnapshotId,
        request: this.getFlightPayload(),
        validation: this.currentValidation
      })
    },
    async createBooking(mode) {
      if (!this.canSelect || !this.canContinueValidation || this.bookingLoading || !this.bookingLeadId) return
      const payload = {
        search_snapshot_id: this.searchSnapshotId,
        flight_snapshot_id: this.currentValidation.flightSnapshotId,
        lead_id: this.bookingLeadId
      }
      if (mode === 'manual') payload.initial_action = 'provisional'
      this.bookingLoading = mode
      this.bookingError = ''
      try {
        const response = await (mode === 'manual' ? postFlightsCreateManualBooking(payload) : postFlightsCreateBooking(payload))
        const id = response.data && response.data.id
        if (!Number.isInteger(id) || id <= 0) throw new Error('The server did not return a booking ID. Check the booking list before retrying.')
        this.$modal.hide(this.validationModalName)
        this.$emit('booking-created', response.data)
        await this.$router.push({ name: 'flights-bookings-detail', params: { id } })
      } catch (error) {
        this.bookingError = error.response?.data?.details || error.response?.data?.detail || error.message || 'Unable to create booking. Please try again.'
      } finally {
        this.bookingLoading = false
      }
    },
    resetFilters() {
      this.filters.sort = this.sortOptions[0]
      this.filters.airline = null
      this.resetDepartureTimeFilter()
    },
    disablePastDates(date) {
      return this.normalizeDate(date) <= this.normalizeDate(new Date())
    },
    disableReturnDates(date) {
      if (!this.form.departure) return true
      return this.normalizeDate(date) < this.normalizeDate(this.form.departure)
    },
    normalizeDate(value) {
      const date = new Date(value)
      date.setHours(0, 0, 0, 0)
      return date
    },
    addDays(value, days) {
      const date = this.normalizeDate(value)
      date.setDate(date.getDate() + days)
      return formatDateValue(date)
    },
    resetDepartureTimeFilter() {
      this.filters.departureFrom = this.departureTimeBounds.min
      this.filters.departureTo = this.departureTimeBounds.max
    },
    onDepartureFromInput() {
      if (this.filters.departureFrom > this.filters.departureTo) {
        this.filters.departureTo = this.filters.departureFrom
      }
    },
    onDepartureToInput() {
      if (this.filters.departureTo < this.filters.departureFrom) {
        this.filters.departureFrom = this.filters.departureTo
      }
    },
    formatMinutes(value) {
      const hours = Math.floor(value / 60)
      const minutes = value % 60
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },
    totalPrice(fare) {
      return fare.segmentTotal
    },
    seatAvailabilityClass(seats) {
      if (seats <= 0) return 'seat-availability--unavailable'
      if (seats <= 5) return 'seat-availability--critical'
      if (seats <= 9) return 'seat-availability--warning'
      return 'seat-availability--good'
    },
    seatAvailabilityLabel(seats) {
      return seats > 0 ? `${seats} seats available` : 'Sold out'
    },
    formatCalculatedPrice(fare, passengerType, field) {
      return this.formatMoney(fare.calculatedPrice?.[passengerType]?.[field], fare.currency)
    },
    formatMoney(value, currency) {
      if (!currency || !Number.isFinite(Number(value))) return '—'

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value)
    },
    formatTime(value) {
      return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(value)
    },
    formatDate(value) {
      return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(value)
    },
    formatSearchDate(value) {
      const date = new Date(`${value}T00:00:00`)
      return this.formatDate(date)
    },
    formatDuration(value) {
      const minutes = Math.floor(value / 60000)
      return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
    },
    airlineInitials(value) {
      return value.split(' ').slice(0, 2).map(word => word[0]).join('')
    },
    onLogoError(event) {
      event.target.style.display = 'none'
    }
  }
}
</script>

<style lang="sass" scoped>
.aviasales
  max-width: 1300px

.aviasales__panel
  margin-top: 25px
  padding: 24px
  border: 1px solid #d7e0e8
  border-radius: 10px
  background-color: #f6f8fa
  box-shadow: none

.aviasales__trip-types
  display: flex
  gap: 25px
  margin-bottom: 22px

.aviasales__route-row
  display: grid
  grid-template-columns: minmax(220px, 1fr) 38px minmax(220px, 1fr)
  align-items: flex-end
  gap: 14px

  .form-group
    margin-bottom: 0

.aviasales__swap
  width: 38px
  height: 38px
  margin-bottom: 0
  border: 1px solid $color-border
  border-radius: 50%
  background-color: #fff
  color: $color-theme
  font-size: 16px
  transition: border-color 0.3s, background-color 0.3s

  &:hover
    border-color: $color-theme
    background-color: rgba($color-theme, 0.06)

.aviasales__details-row
  display: flex
  align-items: flex-end
  gap: 14px
  margin-top: 22px

  .form-group
    margin-bottom: 0

.aviasales__details-field
  flex: 1 1 160px

.aviasales__passenger-field
  flex: 0 0 100px

.aviasales__search-button
  flex: 0 0 auto
  margin-left: auto

.aviasales__search-error
  margin-top: 16px
  padding: 12px 14px
  border-left: 3px solid $color-error
  border-radius: 6px
  background-color: rgba($color-error, 0.08)
  color: $color-error
  font-size: 14px
  line-height: 1.4

.aviasales__selection
  display: flex
  align-items: center
  justify-content: space-between
  gap: 20px
  margin-top: 20px
  padding: 16px 20px
  border-left: 4px solid $color-theme
  border-radius: 6px
  background-color: rgba($color-theme, 0.08)

  span
    color: $color-text-second
    font-size: 14px

.aviasales__selection-price
  color: $color-theme
  font-size: 18px
  font-weight: 600

.aviasales__selected-legs
  flex: 1 1 auto
  min-width: 0

.aviasales__selected-legs--return
  display: grid
  grid-template-columns: repeat(2, minmax(250px, 1fr))
  gap: 10px

.aviasales__selected-leg
  display: flex
  flex-direction: column
  gap: 8px
  padding: 12px 14px
  border: 1px solid rgba($color-theme, 0.16)
  border-radius: 8px
  background-color: rgba(#fff, 0.7)

.aviasales__selected-leg-heading
  display: flex
  align-items: center
  gap: 9px

.aviasales__selected-leg-label
  flex: 0 0 auto
  padding: 3px 7px
  border-radius: 4px
  font-size: 11px !important
  font-weight: 600
  line-height: 1.2
  text-transform: uppercase

.aviasales__selected-leg-label--outbound
  background-color: rgba(#35a4ea, 0.11)
  color: #2779ad !important

.aviasales__selected-leg-label--return
  background-color: rgba($color-theme, 0.12)
  color: $color-theme !important

.aviasales__selected-leg-meta
  display: flex
  align-items: center
  gap: 8px

  span + span
    padding-left: 8px
    border-left: 1px solid rgba($color-text-second, 0.25)

.aviasales__selection-controls
  display: flex
  align-items: center
  gap: 12px

.aviasales__results-heading
  margin: 28px 0 20px
  padding: 18px 20px
  border: 1px solid rgba($color-theme, 0.22)
  border-left: 4px solid $color-theme
  border-radius: 8px
  background-color: rgba($color-theme, 0.04)

.aviasales__step-heading
  display: flex
  align-items: center
  gap: 16px

.aviasales__step
  display: flex
  align-items: center
  gap: 12px

  h2
    margin: 0
    color: #939393
    font-size: 20px
    line-height: 1.2

.aviasales__step--active
  h2
    color: $color-text-base

  .aviasales__step-number
    background-color: $color-theme

.aviasales__step-number
  display: inline-flex
  align-items: center
  justify-content: center
  width: 34px
  height: 34px
  border-radius: 50%
  background-color: #b3b3b3
  color: #fff !important
  font-size: 16px !important
  font-weight: 600

.aviasales__step-arrow
  flex: 0 0 auto
  width: 17px
  height: 8px

.aviasales__empty-return
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 8px
  padding: 24px
  border: 1px solid rgba($color-error, 0.25)
  border-radius: 8px
  background-color: rgba($color-error, 0.06)

  span
    margin-bottom: 6px
    color: $color-text-second
    font-size: 14px

.aviasales-total-badge
  display: inline-flex
  align-items: center
  width: fit-content
  padding: 6px 10px
  border: 1px solid rgba($color-theme, 0.2)
  border-radius: 999px
  background-color: rgba($color-theme, 0.1)
  color: $color-theme
  font-size: 18px
  font-weight: 600
  line-height: 1
  white-space: nowrap

.aviasales__summary
  display: flex
  align-items: center
  justify-content: space-between
  margin-bottom: 16px
  padding-bottom: 14px
  border-bottom: 1px solid rgba($color-theme, 0.16)

  > div
    display: flex
    align-items: baseline
    gap: 12px

  strong
    font-size: 16px

  span
    color: $color-text-second
    font-size: 14px

.aviasales__summary--standalone
  margin: 28px 0 18px
  padding-bottom: 0
  border-bottom: 0

.aviasales__results-layout
  display: grid
  grid-template-columns: 230px 1fr
  align-items: start
  gap: 20px

.aviasales__results-layout--no-filters
  grid-template-columns: minmax(0, 1fr)

.aviasales__filters
  position: sticky
  top: 20px
  padding: 20px
  border: 1px solid #d7e0e8
  border-radius: 8px
  background-color: #f6f8fa

  .checkbox
    margin-bottom: 22px

.aviasales__filters-title
  margin-bottom: 20px
  font-size: 16px
  font-weight: 600

.departure-time-filter
  padding: 2px 2px 8px

.departure-time-filter__single
  display: flex
  flex-direction: column
  align-items: center
  gap: 3px
  padding: 10px
  border: 1px solid rgba(#35a4ea, 0.2)
  border-radius: 6px
  background-color: rgba(#35a4ea, 0.06)

  strong
    color: #226f9f
    font-size: 15px

  span
    color: $color-text-second
    font-size: 12px

.departure-time-filter__values
  display: flex
  align-items: center
  justify-content: space-between
  color: $color-text-second
  font-size: 14px
  font-weight: 600

.departure-time-filter__slider
  position: relative
  height: 24px
  margin-top: 5px

  input
    position: absolute
    z-index: 2
    top: 0
    left: 0
    width: 100%
    height: 24px
    margin: 0
    appearance: none
    background: transparent
    pointer-events: none

    &:last-of-type
      z-index: 3

    &::-webkit-slider-runnable-track
      height: 4px
      background: transparent

    &::-webkit-slider-thumb
      width: 16px
      height: 16px
      margin-top: -6px
      border: 2px solid #35a4ea
      border-radius: 50%
      appearance: none
      background-color: #fff
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18)
      cursor: pointer
      pointer-events: auto

    &::-moz-range-track
      height: 4px
      background: transparent

    &::-moz-range-thumb
      width: 12px
      height: 12px
      border: 2px solid #35a4ea
      border-radius: 50%
      background-color: #fff
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18)
      cursor: pointer
      pointer-events: auto

    &:disabled::-webkit-slider-thumb,
    &:disabled::-moz-range-thumb
      cursor: default

.departure-time-filter__track,
.departure-time-filter__range
  position: absolute
  top: 10px
  height: 4px
  border-radius: 999px

.departure-time-filter__track
  right: 0
  left: 0
  background-color: #e3e3e3

.departure-time-filter__range
  z-index: 1
  background-color: #35a4ea

.aviasales__results
  display: flex
  flex-direction: column
  gap: 12px

.flight-offer
  position: relative
  display: flex
  flex-direction: column

.flight-card__badges
  position: absolute
  z-index: 2
  top: -9px
  left: 14px
  display: flex
  gap: 6px

.flight-card__badge
  padding: 4px 9px
  border-radius: 999px
  color: #fff
  font-size: 14px
  font-weight: 600
  line-height: 1

.flight-card__badge--cheapest
  background-color: $color-theme

.flight-card__badge--fastest
  background-color: #35a4ea

.flight-card
  display: grid
  grid-template-columns: minmax(190px, 1.2fr) minmax(250px, 1.5fr) minmax(130px, 0.8fr) minmax(145px, 0.7fr)
  align-items: center
  gap: 20px
  padding: 20px
  border: 1px solid $color-border
  border-radius: 8px
  background-color: #fff
  transition: border-color 0.3s, border-radius 0.3s, box-shadow 0.3s
  cursor: pointer

  &:hover, &:focus
    border-color: #b8b8b8
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07)
    outline: none

.flight-card--expanded
  border-color: #b8b8b8
  border-radius: 8px 8px 0 0
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07)

.flight-card__airline
  display: flex
  align-items: center
  gap: 12px

  > div:last-child
    display: flex
    flex-direction: column
    gap: 5px

  strong
    font-size: 14px

  span
    color: $color-text-second
    font-size: 14px
    line-height: 1.35

.flight-card__logo
  position: relative
  flex: 0 0 48px
  display: flex
  align-items: center
  justify-content: center
  width: 48px
  height: 48px
  border: 1px solid $color-border
  border-radius: 50%
  background-color: #f7f7f7
  overflow: hidden

  img
    position: relative
    z-index: 1
    width: 100%
    height: 100%
    object-fit: cover
    background-color: #fff

  span
    position: absolute
    color: $color-text-second
    font-weight: 600

.flight-card__route
  display: grid
  grid-template-columns: auto 1fr auto
  align-items: center
  gap: 12px

  > div:not(.flight-card__duration)
    display: flex
    flex-direction: column
    gap: 4px

    span
      color: #35a4ea
      font-weight: 600

    &:last-child
      text-align: right

  strong
    font-size: 16px

  span
    color: $color-text-second
    font-size: 14px

.flight-card__duration
  display: flex
  flex-direction: column
  align-items: center
  gap: 5px

  span:last-child
    font-size: 13px

  i
    position: relative
    width: 100%
    height: 1px
    background-color: $color-border

    &:before,
    &:after
      content: ''
      position: absolute
      top: -3px
      width: 7px
      height: 7px
      border-radius: 50%

    &:before
      left: 0
      background-color: #35a4ea

    &:after
      right: 0
      background-color: $color-theme

.flight-card__availability
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 7px
  padding-left: 12px
  color: $color-text-second
  font-size: 14px

  span:first-child
    display: inline-flex
    align-items: center
    width: fit-content
    padding: 4px 8px
    border: 1px solid $color-border
    border-radius: 999px
    background-color: #f7f7f7
    line-height: 1
    white-space: nowrap

  span:last-child
    font-weight: 600

.seat-availability--good
  color: #77b017

.seat-availability--warning
  color: #c28a00

.seat-availability--critical
  color: #e85c20

.seat-availability--unavailable
  color: #939393

.status-tag.seat-availability--good
  background-color: #77b017

.status-tag.seat-availability--warning
  background-color: #d6a100

.status-tag.seat-availability--critical
  background-color: #e85c20

.status-tag.seat-availability--unavailable
  background-color: #939393

.flight-card__price
  display: flex
  flex-direction: column
  align-items: flex-end
  gap: 3px

  span
    color: $color-text-second
    font-size: 14px

  strong
    font-size: 16px

.flight-card__fares
  padding: 20px
  border: 1px solid #b8b8b8
  border-top: 0
  border-radius: 0 0 8px 8px
  background-color: #fafafa
  overflow: hidden

.aviasales-fares__list
  display: flex
  flex-direction: column
  gap: 12px
  margin-top: 18px

.booking-rules
  margin-top: 16px
  padding: 12px 14px
  border-left: 3px solid #f0c427
  border-radius: 6px
  background-color: rgba(240, 196, 39, 0.12)

  strong
    display: block
    margin-bottom: 6px
    font-size: 14px

  ul
    margin: 0
    padding-left: 20px

  li
    color: $color-text-second
    font-size: 14px
    line-height: 1.4

    & + li
      margin-top: 4px

.booking-rules--validation
  margin-top: 16px

.fare-expand-enter-active, .fare-expand-leave-active
  max-height: 1200px
  opacity: 1
  transform: translateY(0)
  transition: max-height 0.45s ease, opacity 0.3s ease, transform 0.35s ease, padding 0.35s ease, border-width 0.35s ease

.fare-expand-enter, .fare-expand-leave-to
  max-height: 0
  padding-top: 0
  padding-bottom: 0
  border-top-width: 0
  border-bottom-width: 0
  opacity: 0
  transform: translateY(-8px)

.fare-card
  padding: 17px
  border: 1px solid $color-border
  border-radius: 8px
  background-color: #fff

.fare-card--selectable
  cursor: pointer

.fare-card--selected
  border-color: #35a4ea
  background-color: rgba(53, 164, 234, 0.05)
  box-shadow: 0 0 0 1px rgba(53, 164, 234, 0.25)

.fare-card--disabled
  cursor: not-allowed
  opacity: 0.6

.fare-card__heading
  display: grid
  grid-template-columns: minmax(150px, auto) 1fr
  align-items: center
  gap: 15px

  span
    color: $color-text-second
    font-size: 14px

.fare-card__heading > div:first-child
  display: flex
  align-items: baseline
  flex-wrap: wrap
  gap: 6px

.fare-card__details
  display: flex
  flex-wrap: wrap
  justify-content: flex-end
  gap: 18px
  color: $color-text-second
  font-size: 14px

.flight-deadlines
  min-width: 0
  line-height: 1.5

  > span
    display: block
    overflow-wrap: anywhere

.fare-card__deadlines
  display: flex
  flex-wrap: wrap
  gap: 6px 20px
  margin-top: 12px
  color: $color-text-second
  font-size: $font-size-base

  strong
    font-weight: 500

.fare-card__footer
  display: flex
  align-items: center
  flex-wrap: wrap
  justify-content: space-between
  gap: 15px
  margin-top: 15px
  padding-top: 12px
  border-top: 1px solid #eee

  span
    color: $color-text-second
    font-size: 14px

  strong
    font-size: 18px

.fare-card__total
  display: flex
  flex-direction: column
  gap: 4px

.fare-card__actions
  display: flex
  align-items: center
  gap: 8px

.fare-card__purchase
  display: flex
  align-items: flex-end
  gap: 16px

.fare-card__seats
  font-size: 14px
  font-weight: 600
  white-space: nowrap

  &.seat-availability--good
    color: #77b017

  &.seat-availability--warning
    color: #c28a00

  &.seat-availability--critical
    color: #e85c20

  &.seat-availability--unavailable
    color: #939393

.fare-card__notification
  margin-top: 12px
  padding: 10px 12px
  border-left: 3px solid #f0c427
  border-radius: 6px
  background-color: rgba(240, 196, 39, 0.12)
  color: $color-text-second
  font-size: 14px
  line-height: 1.4

@media (max-width: 1350px)
  .flight-card
    grid-template-columns: minmax(170px, 1fr) minmax(230px, 1.4fr) minmax(130px, 0.7fr)

  .flight-card__availability
    display: none

@media (max-width: 1250px)
  .aviasales__details-row
    flex-wrap: wrap

  .aviasales__details-field
    flex-basis: calc(25% - 10px)

@media (max-width: 980px)
  .aviasales__route-row
    position: relative
    grid-template-columns: repeat(2, minmax(0, 1fr))
    column-gap: 52px

    .form-group:nth-of-type(1)
      grid-column: 1

    .form-group:nth-of-type(2)
      grid-column: 2

  .aviasales__swap
    position: absolute
    z-index: 2
    top: 27px
    left: 50%
    transform: translateX(-50%)

  .aviasales__results-layout
    grid-template-columns: 1fr

  .aviasales__filters
    position: static
    display: grid
    grid-template-columns: repeat(3, 1fr)
    gap: 15px

  .aviasales__filters-title
    grid-column: 1 / -1
    margin-bottom: 0

  .flight-card
    grid-template-columns: minmax(170px, 1fr) minmax(230px, 1.5fr) minmax(140px, 0.8fr)

@media (max-width: 700px)
  .aviasales__panel
    padding: 18px

  .aviasales__route-row
    grid-template-columns: 1fr
    column-gap: 14px

    .form-group:nth-of-type(1), .form-group:nth-of-type(2)
      grid-column: auto

  .aviasales__swap
    display: none

  .aviasales__details-field
    flex-basis: 100%

  .aviasales__passenger-field
    flex: 1 1 calc(33.333% - 10px)

  .aviasales__search-button
    width: 100%
    margin-left: 0

  .aviasales__summary
    align-items: flex-start
    gap: 10px

    > div
      flex-direction: column
      gap: 5px

  .aviasales__selection
    align-items: stretch
    flex-direction: column

  .aviasales__selected-legs--return
    grid-template-columns: 1fr

  .aviasales__filters
    grid-template-columns: 1fr

  .flight-card
    grid-template-columns: 1fr

  .flight-card__price
    align-items: flex-start

  .fare-card__heading
    grid-template-columns: 1fr

  .fare-card__details
    justify-content: flex-start

  .fare-card__controls
    flex-wrap: wrap
    justify-content: flex-end

  .aviasales__selection
    align-items: flex-start
    flex-direction: column
</style>

<style lang="sass">
.aviasales
  font-size: 14px

  .title
    font-size: 24px

  .form-group__label,
  .form-group__error,
  .input,
  .radio__text,
  .checkbox__text,
  .no-results__title,
  .no-results__text
    font-size: 14px

  .btn
    font-size: 15px

  .btn--small
    font-size: 14px

  .select
    .vs__search,
    .vs__search::placeholder,
    .vs__selected,
    .select__item,
    .select__noty,
    .vs__dropdown-menu li
      font-size: 14px

  .datepicker .mx-input
    font-size: 14px

.aviasales-datepicker-popup
  font-size: 14px

.aviasales-validation
  color: $color-text-base
  font-size: 14px
  overflow-wrap: anywhere

  .popup__title
    margin-bottom: 22px
    padding-right: 24px
    font-size: 24px

  .popup__body
    font-size: 14px

  // Lucide icons from the supplied HTML reference (ISC license).
  .validation-icon
    display: inline-block
    flex: 0 0 16px
    vertical-align: middle

  .validation-result__title
    display: flex
    align-items: center
    gap: 8px

    .validation-icon
      color: $color-theme

  .validation-result--changed .validation-result__title .validation-icon
    color: #c26712

  .validation-result--error .validation-result__title .validation-icon
    color: $color-error

  .validation-leg__unchanged .validation-icon
    color: $color-theme

  .validation-chevron
    display: inline-flex
    align-items: center
    flex-shrink: 0
    transition: transform .15s ease

  details[open] > summary .validation-chevron
    transform: rotate(90deg)

  .validation-loading
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 12px
    min-height: 200px
    padding: 24px 16px
    text-align: center

    img
      width: 40px
      height: 40px

    strong
      font-size: 16px

    span
      color: $color-text-second
      line-height: 1.5

  .validation-result
    padding: 16px
    border: 1px solid rgba($color-theme, .3)
    border-radius: 8px
    background: rgba($color-theme, .08)

    > strong
      font-size: 16px

    p
      margin: 6px 0 0
      line-height: 1.5

  .validation-result--changed
    border-color: rgba(#e85c20, .3)
    background: rgba(#e85c20, .06)

  .validation-result--error
    border-color: rgba($color-error, .4)
    background: rgba($color-error, .06)

  .validation-leg
    margin-top: 16px
    padding: 20px
    border: 1px solid rgba($color-theme, .22)
    border-radius: 10px
    background: linear-gradient(180deg, rgba($color-theme, .045), #fff 100px)
    box-shadow: 0 3px 12px rgba($color-text-base, .035)

  .validation-leg--return
    background: linear-gradient(180deg, rgba(#35a4ea, .045), #fff 100px)

  .validation-leg__heading, .validation-leg__meta, .validation-leg__price
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 10px

  .validation-leg__heading
    flex-wrap: nowrap
    gap: 12px
    font-size: 13px

  .validation-leg__logo
    width: 38px
    height: 38px
    object-fit: contain
    flex-shrink: 0

  .validation-leg__emblem
    display: flex
    flex: 0 0 38px
    align-items: center
    justify-content: center
    height: 38px
    border: 1px solid rgba($color-theme, .18)
    border-radius: 10px
    background: #fff
    color: $color-theme
    font-size: 23px

  .validation-leg__airline
    display: flex
    flex: 1
    min-width: 0
    flex-direction: column
    gap: 5px

    strong
      font-size: 14px

    > span
      color: $color-text-second
      font-size: 12px

  .validation-leg__badges
    display: flex
    align-items: center
    flex-shrink: 0
    gap: 6px
    text-align: right
    font-size: 12px

  .validation-leg__status
    display: inline-flex
    align-items: center
    padding: 5px 9px
    border-radius: 5px
    font-weight: 600

    &.validation-leg__changed
      background: rgba(#c26712, .1)

    &.validation-leg__critical
      background: rgba($color-error, .1)

  .validation-leg__direction
    display: inline-flex
    align-items: center
    gap: 6px
    padding: 5px 9px
    border-radius: 5px
    background: rgba($color-theme, .1)
    color: $color-theme
    font-weight: 600

  .validation-leg--return .validation-leg__direction
    background: rgba(#35a4ea, .1)
    color: #2779ad

  .validation-leg__changed
    color: #c26712

  .validation-leg__critical
    color: $color-error

  .validation-leg__route
    display: grid
    grid-template-columns: minmax(0, 1fr) 90px minmax(0, 1fr)
    gap: 16px
    margin: 24px 0 18px

    > div
      display: flex
      flex-direction: column
      align-items: flex-start
      gap: 5px

    strong
      font-size: 28px
      line-height: 1.2
      font-weight: 600
      letter-spacing: -.5px

    > div:last-child
      align-items: flex-end
      text-align: right

  .validation-leg__code
    color: #2779ad
    font-size: 15px
    font-weight: 600
    letter-spacing: 1px

  .validation-leg__airport
    max-width: 240px
    color: $color-text-second
    font-size: 12px
    line-height: 1.45

  .validation-leg__connection
    align-self: center
    display: flex
    flex-direction: column
    color: $color-text-second

  .validation-leg__route-line
    display: flex
    align-items: center
    gap: 7px
    margin: 7px 0

    &:before, &:after
      content: ''
      height: 1px
      background: $color-border
      flex: 1

    svg
      flex: 0 0 16px
      display: block

  .validation-leg__flight-type
    font-size: 12px
    line-height: 1.4
    text-align: center

  .validation-leg__meta
    gap: 6px
    color: $color-text-second
    font-size: 12px

    > span
      display: inline-flex
      align-items: center
      gap: 6px
      padding: 5px 8px
      border: 1px solid rgba($color-border, .5)
      border-radius: 5px
      background: #f8f9fa

  .validation-leg__meta > .validation-leg__via
    flex: 1 1 200px
    min-width: 0
    overflow-wrap: anywhere

  .validation-leg__price
    justify-content: space-between
    margin-top: 18px
    padding-top: 14px
    border-top: 1px dashed $color-border

    span
      color: $color-text-second
      font-size: 12px

    strong
      padding: 7px 10px
      border: 1px solid rgba($color-theme, .18)
      border-radius: 6px
      background: rgba($color-theme, .07)
      color: $color-theme
      font-size: 19px

  .validation-differences
    margin-top: 16px
    padding: 4px 12px
    border-radius: 6px
    background: rgba(#e85c20, .06)

  .validation-difference
    display: grid
    grid-template-columns: minmax(100px, 1fr) minmax(0, 2fr)
    gap: 12px
    padding: 9px 0
    line-height: 1.45

    > span
      text-align: right

  .validation-before
    color: $color-text-second
    text-decoration: line-through

  .validation-leg__unchanged
    display: flex
    align-items: center
    gap: 7px
    margin: 12px 0 0
    color: $color-text-second
    font-size: 12px

  .validation-details
    margin-top: 12px
    padding: 14px 0

    summary
      display: flex
      align-items: center
      gap: 7px
      list-style: none
      cursor: pointer
      font-weight: 600

      &::-webkit-details-marker
        display: none

    p
      margin: 12px 0
      color: $color-text-second
      line-height: 1.5

    .validation-direction
      display: inline-block
      margin: 16px 0 10px
      padding: 5px 9px
      border-radius: 5px
      background: rgba($color-theme, .1)
      color: $color-theme
      font-size: 12px
      font-weight: 600

      &--return
        background: rgba(#35a4ea, .1)
        color: #2779ad

  .validation-terms
    white-space: pre-line

  .validation-breakdown
    margin-top: 12px
    overflow-x: auto

    caption
      text-align: left
      padding: 8px 0
      font-weight: 600

    table
      width: 100%
      border-collapse: collapse

    th, td
      padding: 12px 10px
      text-align: right
      border-bottom: 1px solid $color-border

      &:first-child
        padding-left: 0
        text-align: left

      &:last-child
        padding-right: 0

    th
      font-weight: 400

    thead th
      color: $color-text-second

    tbody th, td
      white-space: nowrap

    tbody tr:last-child
      th, td
        border-bottom: 0

  .validation-total
    display: flex
    justify-content: space-between
    flex-wrap: wrap
    gap: 16px
    margin-top: 20px
    padding: 18px
    border-radius: 8px
    background: rgba($color-theme, .08)

    > div
      display: flex
      flex-direction: column
      gap: 8px

    span
      color: $color-text-second
      font-size: 12px

    .flight-deadlines > span
      font-size: $font-size-base

    strong
      font-size: 22px

  .validation-actions
    display: flex
    justify-content: space-between
    flex-wrap: wrap
    gap: 12px
    margin-top: 24px

  summary:focus-visible, button:focus-visible
    outline: 2px solid $color-theme
    outline-offset: 3px

  @media (max-width: 600px)
    padding: 24px 16px

    .validation-leg
      padding: 14px

    .validation-leg__heading
      flex-wrap: wrap

    .validation-leg__badges
      flex-direction: row
      align-items: center

    .validation-leg__route
      grid-template-columns: minmax(0, 1fr) 64px minmax(0, 1fr)
      gap: 8px

      strong
        font-size: 25px

    .validation-difference
      grid-template-columns: 1fr
      gap: 4px

      > span
        text-align: left

    .validation-actions .btn
      flex: 1 1 auto
</style>
